/**
 * Eligible-person person properties.
 *
 * The Iterable projects key every profile on the eligibility person id (the
 * matcher's per-company DE-61 anchor that Galapagos stores as
 * `member_eligibilities.eligible_person_id`). The PostHog → Iterable
 * destination reads it from the member's PostHog person, so the person-sync
 * consumer writes it there with the client it belongs to: the id is stable
 * only within one company and is never comparable across companies.
 *
 * Both values are opaque upstream identifiers, never PII.
 */

/** Person-property keys as they appear in the analytics store. */
export const ELIGIBLE_PERSON_PERSON_PROPERTY_KEYS = {
  eligiblePersonId: 'eligible_person_id',
  eligiblePersonClientRef: 'eligible_person_client_ref',
} as const;

export interface EligiblePersonInput {
  /** The matcher's per-company identity anchor. */
  readonly eligiblePersonId: string;
  /** The provider-qualified client ref (`<source>:<ref>`) of the grant row it came from. */
  readonly clientRef: string | null;
}

export interface EligiblePersonProperties {
  readonly [ELIGIBLE_PERSON_PERSON_PROPERTY_KEYS.eligiblePersonId]?: string;
  readonly [ELIGIBLE_PERSON_PERSON_PROPERTY_KEYS.eligiblePersonClientRef]?: string;
}

/**
 * Derive the eligible-person properties for one member.
 *
 * A key whose source is absent is omitted, never written null: the database
 * only ever fills the anchor null → value, so a later send can only add it,
 * and a member with no anchor yet keeps no property for the destination to
 * filter on.
 */
export function deriveEligiblePersonProperties(
  input: EligiblePersonInput | null,
): EligiblePersonProperties {
  if (input === null || input.eligiblePersonId.length === 0) return {};
  return {
    [ELIGIBLE_PERSON_PERSON_PROPERTY_KEYS.eligiblePersonId]: input.eligiblePersonId,
    ...(input.clientRef
      ? { [ELIGIBLE_PERSON_PERSON_PROPERTY_KEYS.eligiblePersonClientRef]: input.clientRef }
      : {}),
  };
}
